import React, {useEffect, useState, useCallback, useRef, useContext, lazy, Suspense} from 'react'
import { GlobalContext, GlobalProvider } from './state'
import { useNavigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import debounce from './utils/debounce'
import Layout from './components/Layout/Layout'
import LoginPage from './components/Pages/LoginPage'
import {
  Routes, Route
} from "react-router-dom";
import {ModalProvider} from './components/Modal/Modal';
import useAutoUpdateCheck from './utils/UpdateCheck';
import { StreamProvider } from "./context/StreamContext";
import {Navigate} from "react-router-dom";
import cbapi from "./utils/cbapi";
import {createTheme, GlobalStyles, ThemeProvider, Box, CircularProgress} from '@mui/material';

// Seiten werden lazy geladen, damit die schweren Abhängigkeiten (TradingView-Wrapper,
// AmCharts, Tabulator, ...) nicht im Initial-Bundle landen.
const DashboardPage = lazy(() => import('./components/Pages/Dashboard/DashboardPage'));
const AccountPage = lazy(() => import('./components/Pages/AccountPage/AccountPage'));
const AccountDetailPage = lazy(() => import('./components/Pages/AccountPage/AccountDetailPage'));
const ThreeCmaxbotPage = lazy(() => import('./components/Pages/ThreeCmaxbotPage/ThreeCmaxbotPage'));
const ThreeCmaxbotDetailPage = lazy(() => import('./components/Pages/ThreeCmaxbotPage/ThreeCmaxbotDetailPage'));
const SmartbotPage = lazy(() => import('./components/Pages/SmartbotPage/SmartbotPage'));
const SmartbotDetailPage = lazy(() => import('./components/Pages/SmartbotPage/SmartbotDetailPage'));
const GridbotPage = lazy(() => import('./components/Pages/GridbotPage/GridbotPage'));
const GridbotDetailPage = lazy(() => import('./components/Pages/GridbotPage/GridbotDetailPage'));
const BacktestPage = lazy(() => import('./components/Pages/BacktestPage/BacktestPage'));
const BacktestDetailPage = lazy(() => import('./components/Pages/BacktestPage/BacktestDetailPage'));
const SignalPage = lazy(() => import('./components/Pages/SignalPage/SignalPage'));
const SignalProcessorPage = lazy(() => import('./components/Pages/SignalPage/SignalProcessorPage'));
const SignalProcessorDetailPage = lazy(() => import('./components/Pages/SignalPage/SignalProcessorDetailPage'));
const BotSetupPage = lazy(() => import('./components/Pages/BotSetupPage'));
const HelpGuidePage = lazy(() => import('./components/Pages/HelpGuidePage'));
const LinksPage = lazy(() => import('./components/Pages/LinksPage'));
const UserPage = lazy(() => import('./components/Pages/UserPage'));
const RiskSettingsPage = lazy(() => import('./components/Pages/RiskSettingsPage/RiskSettingsPage'));
const TerminalPage = lazy(() => import('./components/Pages/TerminalPage/TerminalPage'));
const BotsV3Page = lazy(() => import('./components/Pages/BotsV3Page/BotsV3Page'));
const BotV3DetailPage = lazy(() => import('./components/Pages/BotsV3Page/BotV3DetailPage'));

// Dunkler Lade-Fallback, damit beim Chunk-Nachladen kein weißer Flash entsteht.
function RouteFallback() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#131722',
      }}
    >
      <CircularProgress />
    </Box>
  );
}

// ProtectedRoute: redirects to /login if no token exists in localStorage.
// Prevents unauthenticated users from seeing authenticated page layouts.
function ProtectedRoute({ children }) {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" replace />;
}

// Hauptkomponente, die den GlobalProvider bereitstellt
const themeLight = createTheme({
  palette: {
    background: {
      default: "#e4f0e2"
    }
  }
});

const themeDark = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: "#222222",
      paper: "#2f3338",
    },
    primary: {
      main: "#ee931d",
    },
    text: {
      primary: "#e1eeff",
      secondary: "#abb6c3",
    },
  },
  components: {
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },
    MuiSelect: {
      defaultProps: { variant: 'outlined' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#161a1e',
          '& fieldset': { borderColor: '#5c6671' },
          '&:hover fieldset': { borderColor: '#8a9ab0' },
        },
        input: { color: '#e1eeff' },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: '#abb6c3' },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { color: '#e1eeff' },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        track: { backgroundColor: '#5c6671' },
      },
    },
  },
});

function App() {
  const [isAuth, setIsAuth] = useState(null);

  useEffect(() => {
    async function checkAuth() {
      const result = await isAuthenticated();
      setIsAuth(result);
    }
    checkAuth();
  }, []);

  function isAuthenticated() {
    const token = localStorage.getItem('token');
    if (!token) return Promise.resolve(false);

    return cbapi.fetch('validate')
      .then((response) => {
        if (response.ok) {
          return response.json().then(data => {
            if (data.status === 'ok') {
              return true;
            }
            return false;
          });
        } else {
          return false;
        }
      })
      .catch(() => false);
  }

  useAutoUpdateCheck();

  if (isAuth === null) {
    return <div>Loading...</div>;
  }

  return (
    <ThemeProvider theme={themeDark}>
    <ModalProvider>
      <GlobalProvider>
        <AppContent isAuth={isAuth} />
      </GlobalProvider>
    </ModalProvider>
    </ThemeProvider>
  );
}

// Inhalt der App, der auf den GlobalContext zugreift
function AppContent({ isAuth }) {
  const { globalState, setGlobalState } = useContext(GlobalContext);
  const [mainContainerHeight, setMainContainerHeight] = useState(0);
  const mainContainerRef = useRef();
  const navigate = useNavigate();
  const [queryClient] = useState(() => new QueryClient());

  const getMainContainerHeight = (appContainer) => {
    return appContainer.getElementsByTagName('main')[0].getBoundingClientRect().height;
  }

  const onRefChange = useCallback((node) => {
    if (node !== null) {
      mainContainerRef.current = node;
      setMainContainerHeight(getMainContainerHeight(node));
    }
  }, []);

  const resizeListener = useCallback((evt) => {
    if (mainContainerRef.current) {
      setMainContainerHeight(getMainContainerHeight(mainContainerRef.current));
    }
  }, []);

  const toggleMenuActive = (evt) => {
    evt.preventDefault();
    const { currentTarget } = evt;
    if (currentTarget.tagName === 'A') {
      setGlobalState((prev) => ({
        ...prev,
        activeMenuItem: currentTarget.parentElement.dataset.itemName,
        isMenuActive: !prev.isMenuActive,
      }));
      navigate(currentTarget.dataset.itemPath);
    } else {
      setGlobalState((prev) => {
        const newState = {
          ...prev,
          isMenuActive: !prev.isMenuActive,
        };
        return newState;
      });
    }
  };

  useEffect(() => {
    window.addEventListener('resize', debounce(resizeListener));
    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, [resizeListener]);

  return (
    <StreamProvider>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route
            path="/"
            element={
              isAuth
                ? <Navigate to="/dashboard" replace />
                : <Navigate to="/login" replace />
            }
          />
          <Route path="/login" element={<LoginPage />} />
          <Route
            element={<ProtectedRoute><Layout ref={onRefChange} toggleMenuActive={toggleMenuActive} /></ProtectedRoute>}
          >
            <Route
              path="/dashboard"
              element={<DashboardPage mainContainerHeight={mainContainerHeight} />}
            />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/account/:id" element={<AccountDetailPage />} />
            <Route path="/3cmaxbot" element={<ThreeCmaxbotPage />} />
            <Route path="/3cmaxbot/:id" element={<ThreeCmaxbotDetailPage />} />
            <Route path="/smartbot" element={<SmartbotPage />} />
            <Route path="/smartbot/:id" element={<SmartbotDetailPage />} />
            <Route
              path="/gridbot"
              element={<GridbotPage mainContainerHeight={mainContainerHeight} />}
            />
            <Route path="/gridbot/:id" element={<GridbotDetailPage Global={globalState} />} />
            <Route
              path="/backtest"
              element={<BacktestPage mainContainerHeight={mainContainerHeight} />}
            />
            <Route
              path="/backtest/:backtestId/smartbot/:id"
              element={<SmartbotDetailPage mainContainerHeight={mainContainerHeight} />}
            />
            <Route path="/backtest/add/:bot_id" element={<BacktestDetailPage mainContainerHeight={mainContainerHeight} />} />
            <Route path="/backtest/:id" element={<BacktestDetailPage mainContainerHeight={mainContainerHeight} />} />
            <Route path="/signal" element={<SignalPage mainContainerHeight={mainContainerHeight} />} />
            <Route path="/signalprocessor" element={<SignalProcessorPage mainContainerHeight={mainContainerHeight} />} />
            <Route path="/signalprocessor/:id" element={<SignalProcessorDetailPage mainContainerHeight={mainContainerHeight} />} />
            <Route path="/terminal" element={<TerminalPage />} />
            <Route path="/terminal/:accountId" element={<TerminalPage />} />
            <Route path="/terminal/:accountId/:symbol" element={<TerminalPage />} />
            <Route path="/terminal/:accountId/:symbol/:symbol2" element={<TerminalPage />} />
            <Route path="/botsv3" element={<BotsV3Page />} />
            <Route path="/botsv3/:id" element={<BotV3DetailPage />} />
            <Route path="/bot-setup" element={<BotSetupPage />} />
            <Route path="/help" element={<HelpGuidePage />} />
            <Route path="/links" element={<LinksPage />} />
            <Route path="/risk-settings" element={<RiskSettingsPage />} />
            <Route path="/user" element={<UserPage />} />
          </Route>
        </Routes>
        </Suspense>
      </QueryClientProvider>
    </StreamProvider>
  );
}

export default App;